import { create } from 'zustand';

import {
  getResolvedCanvasShortcutCatalog,
  setCanvasShortcutCatalogReader,
  type CanvasShortcutCatalogItem,
} from '@/lib/canvas/canvas-keyboard-shortcuts';
import {
  isRetryableShortcutSyncError,
  mergeHydrateCanvasOverrides,
  resolveCanvasShortcutCatalog,
  sanitizeCanvasOverrideMap,
  type CanvasShortcutOverrideMap,
} from '@/lib/canvas/canvas-shortcut-overrides';
import { usersApi } from '@/services/api/users';
import { toast } from '@/hooks/use-toast';

const STORAGE_PREFIX = 'jointo:shortcuts:';
const SESSION_USER_ID_KEY = 'jointo:shortcuts:userId';
const SHORTCUT_CHANNEL_NAME = 'jointo-canvas-shortcuts';
const SYNC_DEBOUNCE_MS = 400;
const TAB_ID = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

interface PersistedShortcutCache {
  canvas: CanvasShortcutOverrideMap;
  pendingSync: boolean;
}

interface CanvasShortcutBroadcast {
  origin: string;
  userId: string;
  canvas: CanvasShortcutOverrideMap;
  pendingSync: boolean;
}

interface CanvasShortcutState {
  userId: string | null;
  canvasOverrides: CanvasShortcutOverrideMap;
  pendingSync: boolean;
  resolvedCatalog: CanvasShortcutCatalogItem[];
  hydrateFromServer: (userId: string, serverCanvas?: CanvasShortcutOverrideMap) => void;
  resetToAnonymousDefaults: () => void;
  setCanvasOverrides: (canvas: CanvasShortcutOverrideMap) => void;
  bindCanvasShortcut: (actionId: keyof CanvasShortcutOverrideMap, keyTokens: string[]) => void;
  resetCanvasShortcut: (actionId: keyof CanvasShortcutOverrideMap) => void;
  resetAllCanvasShortcuts: () => void;
}

let syncTimer: ReturnType<typeof setTimeout> | null = null;
let syncGeneration = 0;
let lastServerCanvas: CanvasShortcutOverrideMap = {};
let shortcutChannel: BroadcastChannel | null | undefined;

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

function readCache(userId: string): PersistedShortcutCache | null {
  if (typeof localStorage === 'undefined' || !userId) return null;
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedShortcutCache;
    return {
      canvas: sanitizeCanvasOverrideMap(parsed.canvas),
      pendingSync: Boolean(parsed.pendingSync),
    };
  } catch {
    return null;
  }
}

function writeCache(userId: string, cache: PersistedShortcutCache): void {
  if (typeof localStorage === 'undefined' || !userId) return;
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(cache));
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(SESSION_USER_ID_KEY, userId);
    }
  } catch {
    // 配额满 / 隐私模式：忽略，不打断录制
  }
}

function clearUserShortcutCache(userId: string | null): void {
  if (userId && typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem(storageKey(userId));
    } catch {
      // ignore
    }
  }
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(SESSION_USER_ID_KEY);
    } catch {
      // ignore
    }
  }
}

function deriveResolved(overrides: CanvasShortcutOverrideMap): CanvasShortcutCatalogItem[] {
  return resolveCanvasShortcutCatalog(overrides);
}

function cancelPendingSync(): void {
  if (syncTimer) {
    clearTimeout(syncTimer);
    syncTimer = null;
  }
  syncGeneration += 1;
}

function getShortcutChannel(): BroadcastChannel | null {
  if (shortcutChannel !== undefined) return shortcutChannel;
  if (typeof BroadcastChannel === 'undefined') {
    shortcutChannel = null;
    return null;
  }
  try {
    shortcutChannel = new BroadcastChannel(SHORTCUT_CHANNEL_NAME);
    shortcutChannel.onmessage = (event: MessageEvent<CanvasShortcutBroadcast>) => {
      applyRemoteCanvasShortcuts(event.data);
    };
  } catch {
    shortcutChannel = null;
  }
  return shortcutChannel;
}

function broadcastCanvasShortcuts(
  userId: string,
  canvas: CanvasShortcutOverrideMap,
  pendingSync: boolean
): void {
  const channel = getShortcutChannel();
  if (!channel) return;
  const message: CanvasShortcutBroadcast = {
    origin: TAB_ID,
    userId,
    canvas,
    pendingSync,
  };
  try {
    channel.postMessage(message);
  } catch {
    // ignore
  }
}

function applyRemoteCanvasShortcuts(message: CanvasShortcutBroadcast | null): void {
  if (!message || message.origin === TAB_ID) return;
  const state = useCanvasShortcutStore.getState();
  if (!state.userId || state.userId !== message.userId) return;
  cancelPendingSync();
  const canvas = sanitizeCanvasOverrideMap(message.canvas);
  if (!message.pendingSync) {
    lastServerCanvas = canvas;
  }
  useCanvasShortcutStore.setState({
    canvasOverrides: canvas,
    pendingSync: message.pendingSync,
    resolvedCatalog: deriveResolved(canvas),
  });
  writeCache(message.userId, { canvas, pendingSync: message.pendingSync });
}

function applyCanvasState(canvas: CanvasShortcutOverrideMap, pendingSync: boolean): void {
  useCanvasShortcutStore.setState({
    canvasOverrides: canvas,
    pendingSync,
    resolvedCatalog: deriveResolved(canvas),
  });
}

async function flushCanvasShortcutSync(
  userId: string,
  canvas: CanvasShortcutOverrideMap
): Promise<void> {
  const generation = ++syncGeneration;
  try {
    const saved = await usersApi.updateMyShortcuts({ canvas });
    if (generation !== syncGeneration) return;
    const nextCanvas = sanitizeCanvasOverrideMap(saved.canvas);
    lastServerCanvas = nextCanvas;
    applyCanvasState(nextCanvas, false);
    writeCache(userId, { canvas: nextCanvas, pendingSync: false });
    broadcastCanvasShortcuts(userId, nextCanvas, false);
  } catch (error) {
    if (generation !== syncGeneration) return;
    if (!isRetryableShortcutSyncError(error)) {
      const fallback = sanitizeCanvasOverrideMap(lastServerCanvas);
      applyCanvasState(fallback, false);
      writeCache(userId, { canvas: fallback, pendingSync: false });
      broadcastCanvasShortcuts(userId, fallback, false);
      toast({
        title: '快捷键未保存',
        description: '该绑定未被接受，已恢复为服务端版本。',
        variant: 'destructive',
      });
      return;
    }
    writeCache(userId, { canvas, pendingSync: true });
    toast({
      title: '快捷键未同步',
      description: '已保存在本机，下次进入画布会再试一次。',
      variant: 'destructive',
    });
  }
}

function scheduleCanvasShortcutSync(userId: string, canvas: CanvasShortcutOverrideMap): void {
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    syncTimer = null;
    void flushCanvasShortcutSync(userId, canvas);
  }, SYNC_DEBOUNCE_MS);
}

export const useCanvasShortcutStore = create<CanvasShortcutState>((set, get) => ({
  userId: null,
  canvasOverrides: {},
  pendingSync: false,
  resolvedCatalog: deriveResolved({}),

  hydrateFromServer: (userId, serverCanvas = {}) => {
    if (!userId) {
      get().resetToAnonymousDefaults();
      return;
    }
    cancelPendingSync();
    const sanitizedServer = sanitizeCanvasOverrideMap(serverCanvas);
    lastServerCanvas = sanitizedServer;
    const local = readCache(userId);
    const canvas = mergeHydrateCanvasOverrides({
      pendingSync: Boolean(local?.pendingSync),
      localCanvas: local?.canvas ?? {},
      serverCanvas: sanitizedServer,
    });
    const pendingSync = Boolean(local?.pendingSync);
    set({
      userId,
      canvasOverrides: canvas,
      pendingSync,
      resolvedCatalog: deriveResolved(canvas),
    });
    writeCache(userId, { canvas, pendingSync });
    if (pendingSync) {
      scheduleCanvasShortcutSync(userId, canvas);
    }
  },

  resetToAnonymousDefaults: () => {
    cancelPendingSync();
    clearUserShortcutCache(get().userId);
    lastServerCanvas = {};
    set({
      userId: null,
      canvasOverrides: {},
      pendingSync: false,
      resolvedCatalog: deriveResolved({}),
    });
  },

  setCanvasOverrides: (canvas) => {
    const userId = get().userId;
    if (!userId) return;
    const next = sanitizeCanvasOverrideMap(canvas);
    set({
      canvasOverrides: next,
      pendingSync: true,
      resolvedCatalog: deriveResolved(next),
    });
    writeCache(userId, { canvas: next, pendingSync: true });
    broadcastCanvasShortcuts(userId, next, true);
    scheduleCanvasShortcutSync(userId, next);
  },

  bindCanvasShortcut: (actionId, keyTokens) => {
    get().setCanvasOverrides({
      ...get().canvasOverrides,
      [actionId]: { keyTokens },
    });
  },

  resetCanvasShortcut: (actionId) => {
    const next = { ...get().canvasOverrides };
    delete next[actionId];
    get().setCanvasOverrides(next);
  },

  resetAllCanvasShortcuts: () => {
    get().setCanvasOverrides({});
  },
}));

setCanvasShortcutCatalogReader(() => useCanvasShortcutStore.getState().resolvedCatalog);

export function applyUserShortcutPreferences(
  user: {
    userId: string;
    shortcuts?: { canvas?: CanvasShortcutOverrideMap };
  } | null
): void {
  if (!user?.userId) {
    useCanvasShortcutStore.getState().resetToAnonymousDefaults();
    return;
  }
  useCanvasShortcutStore.getState().hydrateFromServer(user.userId, user.shortcuts?.canvas ?? {});
}

export function peekResolvedCanvasShortcutCatalog(): readonly CanvasShortcutCatalogItem[] {
  return getResolvedCanvasShortcutCatalog();
}

getShortcutChannel();
